import { type StringInputViewModel } from '@sitecore-jss/sitecore-jss-forms';
import { Label, type ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import classNames from 'classnames/bind';
import { type ChangeEventHandler, type FC, type FocusEventHandler, type MouseEventHandler, useState } from 'react';

import Button, { ButtonVariant } from '../../../helpers/Button/Button';
import { FieldValidationErrors } from './FieldValidationErrors';
import { getFormFieldAriaProps, handleOnChangeWithEvent } from '../Functions';
import { useDictionary } from '../../../../lib/utils';

import styles from '../JssNextForm.module.scss';

const cx = classNames.bind(styles);

export const MemberIdField: FC<ValueFieldProps<StringInputViewModel>> = (props) => {
  const { field, value, onChange, tracker, errors } = props;

  const [currentFieldValue, setCurrentFieldValue] = useState<string>(value);
  const [showFieldValue, setShowFieldValue] = useState<boolean>(false);

  const showMemberIdLabel = useDictionary('Forms-Show-Member-ID', 'Show Member ID');
  const hideMemberIdLabel = useDictionary('Forms-Hide-Member-ID', 'Hide Member ID');

  const handleBlur: FocusEventHandler<HTMLInputElement> = () => {
    tracker.onBlurField(field, currentFieldValue, errors);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const { value } = e.target as HTMLInputElement;

    setCurrentFieldValue(value);

    if (onChange) {
      handleOnChangeWithEvent(field, value, onChange);
    }
  };

  const handleFocus: FocusEventHandler<HTMLInputElement> = () => {
    tracker.onFocusField(field, currentFieldValue);
  };

  const handleShowHideButtonClick: MouseEventHandler<HTMLButtonElement> = () => {
    setShowFieldValue(!showFieldValue);
  };

  const fieldMaskSpacerCharacter = '•';
  const fieldMaskSpacer = !showFieldValue
    ? fieldMaskSpacerCharacter.repeat(currentFieldValue?.length ?? 0)
    : currentFieldValue;

  const fieldMask = 'xxxxxxxxxxx';
  const fieldMaskValue = fieldMask.slice(currentFieldValue?.length);

  const id = field.valueField.id;
  const fieldMaskId = `${id}-mask`;

  return (
    <fieldset className={cx('text-input-mask')}>
      <Label {...props} />
      <div className={cx('text-input-mask__container')}>
        <div aria-hidden="true" className={cx('text-input-mask__mask')}>
          <span className={cx('text-input-mask__spacer')}>{fieldMaskSpacer}</span>
          <span className={cx('text-input-mask__value')} id={fieldMaskId}>
            {fieldMaskValue}
          </span>
        </div>
        <input
          autoComplete="off"
          className={cx('text-input-mask__input', field.model.cssClass)}
          id={id}
          inputMode="numeric"
          maxLength={field.model.maxLength}
          name={field.valueField.name}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={field.model.placeholderText}
          type={showFieldValue ? 'text' : 'password'}
          value={currentFieldValue}
          {...getFormFieldAriaProps(field, errors)}
        />
        <Button
          aria-label={showFieldValue ? hideMemberIdLabel : showMemberIdLabel}
          className={cx('text-input-mask__button')}
          icon={showFieldValue ? 'hide-information' : 'show-information'}
          onClick={handleShowHideButtonClick}
          variant={ButtonVariant.Ghost}
        />
      </div>
      <FieldValidationErrors {...props} />
    </fieldset>
  );
};

export default MemberIdField;
